import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AuthUtils, CloudActions } from '@portal/portal-core';

import { Modal } from 'components/styled-primitives';
import { fjModalSizes, FjButton, FjFlexContainer, FjLinkButton } from 'components/primitives';
import {
  DataList,
  DataListHeader,
  ModalSection,
  ModalSectionWrapper,
} from 'components/modal-components';
import {
  SeatSummary,
  SeatSummaryHelpButton,
  SeatSummaryPanel,
  SeatUsage,
} from 'components/licenses/seat-summary';
import { PRODUCT_INFO } from 'constants/products';
import { formatDateTime, formatEnabledFeatures } from 'utils';
import {
  selectUsers,
  selectArchiveActiveEotStatus,
  selectCostCenterInvitesCache,
  selectCostCenterInvitesCacheFetchFailed,
} from 'selectors/super-admin';
import { countInvitesForProductAccess } from '@/types/cost-center-invite';
import {
  fetchActiveEot,
  archiveActiveEot,
  archiveActiveEotReset,
} from 'ducks/super-client/actions';
import type { AppDispatch } from 'store';

interface StatusColor {
  bg: string;
  text: string;
  border: string;
}

const statusColors: Record<string, StatusColor> = {
  success: { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
  error: { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
  pending: { bg: '#d1ecf1', text: '#0c5460', border: '#bee5eb' },
};

const StatusMessage = styled.div<{ $variant?: string }>`
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  background-color: ${({ $variant }) => (statusColors[$variant ?? ''] || statusColors.pending).bg};
  color: ${({ $variant }) => (statusColors[$variant ?? ''] || statusColors.pending).text};
  border: 1px solid
    ${({ $variant }) => (statusColors[$variant ?? ''] || statusColors.pending).border};
`;

const HeaderWithTrigger = styled.span`
  display: inline-flex;
  align-items: baseline;
`;

const SeatSummaryButton = styled(SeatSummaryHelpButton)`
  align-self: center;
  margin-left: ${({ theme: { grid } }) => grid.units(0.5)};
`;

const CopyJsonTrigger = styled(FjLinkButton)`
  font-size: 13px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.cloudTeal};
  text-decoration: underline dotted;
`;

const LeftColumn = styled(FjFlexContainer).attrs({ direction: 'column' })`
  gap: ${({ theme: { grid } }) => grid.units(1)};
`;

const RightColumn = styled(FjFlexContainer).attrs({ direction: 'column' })`
  gap: ${({ theme: { grid } }) => grid.units(1)};
  overflow-y: auto;
  margin-left: ${({ theme: { grid } }) => grid.units(1)};
`;

interface CostCenter {
  name?: string;
  id?: string;
  owner?: string;
  type?: string;
  startDate?: string | number | Date | null;
  countryCode?: string;
  whitelistIgnore?: boolean;
  whitelistDomains?: string[];
}

// A single user's seat within productAccess.users - device/session limits like
// maxAnnualDeviceDeactivations live here per-user, not on the license/productAccess object
// itself (see FJCLOUD-4364 follow-up: the modal previously read maxAnnualDeviceDeactivations
// off `license`, which never has it, hence it always showed "N/A").
export interface UserSeat {
  suspended?: boolean;
  lastUsed?: string | number | Date | null;
  startDate?: string | number | Date | null;
  endDate?: string | number | Date | null;
  archivedDate?: string | number | Date | null;
  maxBoundDevices?: number;
  maxAnnualDeviceDeactivations?: number;
  maxSimultaenousSessions?: number;
  offlineDurationDays?: number;
}

// A feature entry on productAccess.features (the same data Manage License edits).
interface LicenseFeature {
  name?: string;
  enabled?: boolean;
  valueType?: string;
  value?: string | number | boolean | Date | null;
}

interface License {
  id: string;
  name?: string;
  type?: string;
  activityState?: string;
  active?: unknown;
  startDate?: string | number | Date | null;
  endDate?: string | number | Date | null;
  sortStartDate?: string | number | Date | null;
  sortEndDate?: string | number | Date | null;
  region?: string;
  maxActiveUsers: number;
  users?: Record<string, UserSeat>;
  sharedComputers?: Record<string, unknown>;
  maxSharedComputers?: number;
  authorizedDevicesOverride?: number | null;
  features?: Record<string, LicenseFeature>;
  costCenter?: CostCenter;
}

interface AdminUser {
  id?: string;
  firstname?: string;
  lastname?: string;
  primaryEmail?: string;
}

interface ActiveEot {
  id: string;
  startDate?: string | number | Date | null;
  endDate?: string | number | Date | null;
}

interface EotState {
  fetchStatus: string | null;
  fetchError: string | null;
  eot: ActiveEot | null;
  archiveStatus: string | null;
  archiveError: string | null;
}

interface LicenseDetailModalProps {
  license: License;
  userId?: string;
  onRequestClose: () => void;
}

const LicenseDetailModal = ({
  license,
  userId,
  onRequestClose,
}: LicenseDetailModalProps): React.JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  // `selectUsers` / `selectArchiveActiveEotStatus` read from the untyped `.js` state slice
  // and are typed as `unknown`; narrow to the shapes accessed here.
  const users = useSelector(selectUsers) as AdminUser[];
  const eotState = useSelector(selectArchiveActiveEotStatus) as EotState;
  const costCenterInvitesCache = useSelector(selectCostCenterInvitesCache);
  const invitesFetchFailed = useSelector(selectCostCenterInvitesCacheFetchFailed);
  const isInstitutional = license.costCenter?.type === 'institutional';
  const costCenterId = license.costCenter?.id;

  const adminUser = React.useMemo(
    () => users.find((user) => user.id === license?.costCenter?.owner),
    [license, users],
  );

  React.useEffect(() => {
    if (userId) {
      dispatch(fetchActiveEot(userId, license.id));
    }
    return () => {
      dispatch(archiveActiveEotReset());
    };
  }, [userId, license.id]);

  // See FJCLOUD-4364 follow-up: the Institutional/Individual license tables reuse a cost-center-wide
  // invite count that folds every product on a cost center together, so a multi-product cost
  // center's seat calculations there are inflated by a sibling product's invites. This modal avoids
  // that imprecision differently: it fetches the existing cost-center-scoped invite list (the same
  // ADMIN_GET_COST_CENTER_INVITES action/backend handler other admin views already use) and filters
  // it down to this license's own invites below, since every invite already carries its own
  // productAccessId - no separate per-license backend endpoint is needed.
  // Only institutional licenses render Seat Calculations. The invite list is refetched every time
  // the modal opens, so an invite created or cancelled since the cost center was last cached is not
  // missed; a cached list is shown meanwhile instead of a blank. The effect deliberately does not
  // depend on the cache, or each fetch result would trigger another fetch.
  React.useEffect(() => {
    if (isInstitutional && costCenterId) {
      dispatch(CloudActions.adminGetCostCenterInvites(costCenterId));
    }
  }, [dispatch, costCenterId, isInstitutional]);

  // The pending-invite count behind Allocated/Open Seats is unknown until this cost center's
  // invites have been fetched, so it must not be shown (or copied) as 0 before then or after a
  // failed fetch.
  const invitesStatus: 'ready' | 'loading' | 'error' = React.useMemo(() => {
    if (!isInstitutional || (costCenterId && costCenterId in costCenterInvitesCache)) return 'ready';
    return costCenterId && invitesFetchFailed[costCenterId] ? 'error' : 'loading';
  }, [isInstitutional, costCenterId, costCenterInvitesCache, invitesFetchFailed]);

  const pendingInvitesCount = React.useMemo(
    () => countInvitesForProductAccess(costCenterInvitesCache[costCenterId ?? ''], license.id),
    [costCenterInvitesCache, costCenterId, license.id],
  );

  // The standardized seat-state calculation (AuthUtils.getSeatUsageForProductAccess) - the same
  // one every other seat-count display in the app uses. This modal previously hand-rolled its
  // own "Available Seats" math from raw Object.keys(license.users).length, which counted every
  // user ever added (including suspended/expired ones, never pruned from that map) rather than
  // only currently-active seats - see FJCLOUD-4364.
  const seatUsage = React.useMemo<SeatUsage>(
    () => AuthUtils.getSeatUsageForProductAccess(license, pendingInvitesCount),
    [license, pendingInvitesCount],
  );
  const enabledFeatureLines = React.useMemo(
    () => formatEnabledFeatures(license.features),
    [license.features],
  );
  const [showSeatSummary, setShowSeatSummary] = React.useState(false);
  const productInfo = PRODUCT_INFO[license.name ?? ''];
  const userSeat = userId ? license.users?.[userId] : undefined;
  const seatUser = React.useMemo(() => users.find((user) => user.id === userId), [users, userId]);
  const seatUserName = `${seatUser?.firstname || ''} ${seatUser?.lastname || ''}`.trim();
  // Placeholder for a figure that includes pending invites until they have loaded.
  const invitesDependent = (value: number | undefined): string => {
    if (invitesStatus === 'loading') return '…';
    if (invitesStatus === 'error') return 'Unavailable';
    return String(value ?? 0);
  };
  const formatValueOrNA = (value: number | string | undefined | null): string =>
    value === undefined || value === null ? 'N/A' : String(value);
  const formatDateOrNA = (value: string | number | Date | null | undefined): string =>
    value ? formatDateTime(value) : 'N/A';

  const handleArchiveEot = (): void => {
    // `eot` is guaranteed present when the Archive button that calls this is rendered.
    dispatch(archiveActiveEot(eotState.eot?.id as string));
  };

  const handleArchiveEotReset = (): void => {
    // `userId` is guaranteed present when the Refresh button that calls this is rendered.
    dispatch(fetchActiveEot(userId!, license.id));
  };

  return (
    <Modal
      accept={{
        text: 'OK',
        callback: onRequestClose,
      }}
      size={fjModalSizes.XL}
      showX
      onClickX={onRequestClose}
      allowScroll
    >
      <ModalSectionWrapper>
        <LeftColumn>
          <ModalSection>
            <DataList
              title="Cost Center Details"
              data={[
                ['Name', license.costCenter?.name],
                ['ID', license.costCenter?.id],
                [
                  'Owner',
                  <div style={{ display: 'inline-block' }}>
                    <div>
                      {adminUser
                        ? `${adminUser.firstname || ''} ${adminUser.lastname || ''}`.trim()
                        : license.costCenter?.owner}
                    </div>
                    <div>{adminUser?.primaryEmail}</div>
                    <div style={{ color: '#999', fontSize: '0.85em' }}>
                      {license.costCenter?.owner}
                    </div>
                  </div>,
                ],
                ['Type', license.costCenter?.type],
                ['Created At', formatDateTime(license.costCenter?.startDate)],
                ['Country Code', license.costCenter?.countryCode],
                [
                  'Whitelisted Domains',
                  license.costCenter?.whitelistIgnore
                    ? 'N/A'
                    : license.costCenter?.whitelistDomains?.join(', '),
                ],
              ]}
            />
          </ModalSection>
          <ModalSection>
            <DataList
              title={
                <HeaderWithTrigger>
                  License Details (
                  <CopyJsonTrigger
                    onClick={() =>
                      navigator.clipboard.writeText(
                        JSON.stringify(
                          isInstitutional && invitesStatus === 'ready'
                            ? { ...license, seatCalculations: seatUsage }
                            : license,
                        ),
                      )
                    }
                  >
                    Copy License JSON
                  </CopyJsonTrigger>
                  )
                </HeaderWithTrigger>
              }
              data={[
                ['ID', license.id],
                ['Product', license.name],
                ['Type', license.type],
                ['Activity State', license.activityState],
                ['Active', String(!!license.active)],
                ['Start Date', formatDateTime(license.startDate)],
                ['End Date', formatDateTime(license.endDate)],
                ['Sort Start Date', formatDateTime(license.sortStartDate)],
                ['Sort End Date', formatDateTime(license.sortEndDate)],
                ['Region', license.region],
                ['Authorized Devices Override', license.authorizedDevicesOverride ?? 'N/A'],
              ]}
            />
          </ModalSection>
          <ModalSection>
            <DataListHeader>Product Features</DataListHeader>
            {enabledFeatureLines.length === 0 && (
              <p style={{ fontSize: 14, color: '#777', margin: '8px 0' }}>
                No features enabled for this license.
              </p>
            )}
            {enabledFeatureLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </ModalSection>
          {isInstitutional && (
            <ModalSection>
              <DataListHeader>
                <HeaderWithTrigger>
                  Seat Calculations
                  <SeatSummaryButton
                    expanded={showSeatSummary}
                    onClick={() => setShowSeatSummary((shown) => !shown)}
                  />
                </HeaderWithTrigger>
              </DataListHeader>
              {invitesStatus !== 'ready' && (
                <p style={{ fontSize: 14, color: '#777', margin: '8px 0' }}>
                  {invitesStatus === 'loading'
                    ? 'Loading pending invites…'
                    : 'Pending invites could not be loaded, so seat counts that include them are unavailable.'}
                </p>
              )}
              {showSeatSummary && invitesStatus === 'ready' && (
                <SeatSummaryPanel>
                  <SeatSummary
                    name={productInfo?.name ?? license.name ?? ''}
                    Icon={productInfo?.Icon ?? null}
                    seats={seatUsage}
                    productName={license.name ?? ''}
                  />
                </SeatSummaryPanel>
              )}
              <DataList
                // Every field AuthUtils.getSeatUsageForProductAccess returns, labeled with its own
                // field name rather than a locally-invented one - see FJCLOUD-4364. The prior
                // "Total Seats"/"Available Seats"/"Max Shared Computers"/"Available Shared
                // Computers" labels mapped onto a hand-rolled (and wrong) calculation; this modal
                // shows exactly what the shared utility function computes, nothing else.
                // `getSeatUsageForProductAccess` is untyped JS with no runtime defaults, so `?? 0`
                // guards against ever rendering the literal string "undefined" for a malformed
                // license record (e.g. one missing maxSharedComputers).
                data={[
                  ['Max Seats', String(seatUsage.maxSeats ?? 0)],
                  ['Max Shared', String(seatUsage.maxShared ?? 0)],
                  ['Allocated Seats', invitesDependent(seatUsage.allocatedSeats)],
                  ['Open Seats', invitesDependent(seatUsage.openSeats)],
                  ['Open Shared Seats', String(seatUsage.openSharedSeats ?? 0)],
                  ['Active User Seats', String(seatUsage.activeUserSeats ?? 0)],
                  ['Active Shared Seats', String(seatUsage.activeSharedSeats ?? 0)],
                  ['Pending User Invites', invitesDependent(seatUsage.pendingUserInvites)],
                ]}
              />
            </ModalSection>
          )}
        </LeftColumn>
        <RightColumn>
          {isInstitutional && userSeat && (
            <ModalSection>
              <DataList
                title={seatUserName ? `Seat Details for ${seatUserName}` : 'Seat Details'}
                data={[
                  ['Suspended', String(!!userSeat.suspended)],
                  ['Start Date', formatDateOrNA(userSeat.startDate)],
                  ['End Date', formatDateOrNA(userSeat.endDate)],
                  ['Last Used', formatDateOrNA(userSeat.lastUsed)],
                  ['Archived Date', formatDateOrNA(userSeat.archivedDate)],
                  ['Max Bound Devices', formatValueOrNA(userSeat.maxBoundDevices)],
                  [
                    'Max Annual Device Deactivations',
                    formatValueOrNA(userSeat.maxAnnualDeviceDeactivations),
                  ],
                  ['Max Simultaneous Sessions', formatValueOrNA(userSeat.maxSimultaenousSessions)],
                  ['Offline Duration (Days)', formatValueOrNA(userSeat.offlineDurationDays)],
                ]}
              />
            </ModalSection>
          )}
          {userId && (
            <ModalSection>
              <DataListHeader>
                {eotState.archiveStatus === 'success' ? 'Archived EOT' : 'Active EOT'}
              </DataListHeader>
              {(eotState.fetchStatus === null || eotState.fetchStatus === 'pending') && (
                <p style={{ fontSize: 14, color: '#777', margin: '8px 0' }}>Loading active EOT…</p>
              )}
              {eotState.fetchStatus === 'error' && (
                <StatusMessage $variant="error">{eotState.fetchError}</StatusMessage>
              )}
              {eotState.fetchStatus === 'success' && !eotState.eot && (
                <p style={{ fontSize: 14, color: '#777', margin: '8px 0' }}>
                  No active EOT found for this license.
                </p>
              )}
              {eotState.fetchStatus === 'success' && eotState.eot && (
                <>
                  <DataList
                    data={[
                      ['EOT ID', eotState.eot.id],
                      ['Start Date', formatDateTime(eotState.eot.startDate)],
                      ['End Date', formatDateTime(eotState.eot.endDate)],
                    ]}
                  />
                  {eotState.archiveStatus === 'success' ? (
                    <FjButton
                      type="button"
                      onClick={handleArchiveEotReset}
                      style={{ marginTop: 8 }}
                    >
                      Refresh
                    </FjButton>
                  ) : (
                    <>
                      {eotState.archiveStatus === 'error' && (
                        <StatusMessage $variant="error">{eotState.archiveError}</StatusMessage>
                      )}
                      <FjButton
                        type="button"
                        onClick={handleArchiveEot}
                        disabled={eotState.archiveStatus === 'pending'}
                        style={{ marginTop: 8 }}
                      >
                        {eotState.archiveStatus === 'pending' ? 'Archiving…' : 'Archive Active EOT'}
                      </FjButton>
                    </>
                  )}
                </>
              )}
            </ModalSection>
          )}
        </RightColumn>
      </ModalSectionWrapper>
    </Modal>
  );
};

export default LicenseDetailModal;
